import { Icons } from '@/libs/components/src/lib/icons';
import { Button } from '@chiroup/components/Button';
import { DatabaseTable } from '@chiroup/components/DatabaseTable';
import { DateRangePicker } from '@chiroup/components/DateRangePicker';
import { Loading } from '@chiroup/components/Loading';
import { NoRowsFound } from '@chiroup/components/NoRowsFound';
import { PillButton } from '@chiroup/components/PillButton';
import { ChiroUpDayJsCommon } from '@chiroup/core/constants/stringConstants';
import { ChiroUpJSON } from '@chiroup/core/functions/ChiroUpJSON';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import useDevtools from '../../../../hooks/useDevtools';

type IntegrationRecord = {
  id: number;
  name: string;
  billingProfileId: number;
  type: string;
  active: number;
  production: number;
  step: string;
  controlNumber: number;
  integratedClearinghouse: string;
  lastProcessedAt: number | null;
  credentialsValid: number | null;
  credentialsLastCheckedAt: number | null;
};

type LogRecord = {
  id: string;
  integrationId: number;
  integrationExchangeId?: number;
  level: string;
  message: string;
  data: string;
  stackTrace?: string;
  createdAt: number;
  updatedAt: number;
};

type Props = {
  clinicId: number;
  timezone?: string;
};

const LOG_LEVELS = ['error', 'warn', 'info', 'debug', 'trace'] as const;

const levelColors: Record<string, string> = {
  error: 'bg-red-100 text-red-700',
  warn: 'bg-amber-100 text-amber-700',
  info: 'bg-blue-100 text-blue-700',
  debug: 'bg-gray-100 text-gray-700',
  trace: 'bg-gray-50 text-gray-500',
};

const AutomationPanel: React.FC<Props> = ({ clinicId, timezone }) => {
  const tz = timezone || ChiroUpDayJsCommon.defaultTimezone;
  const { getIntegrations, getIntegrationLogs } = useDevtools('automation-panel') as any;

  const [integrations, setIntegrations] = useState<IntegrationRecord[]>([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationRecord | null>(null);

  const [logs, setLogs] = useState<LogRecord[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [lastKey, setLastKey] = useState<Record<string, any> | null>(null);
  const [prevKeys, setPrevKeys] = useState<Record<string, any>[]>([]);
  const [levelFilter, setLevelFilter] = useState<string | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
  });

  const loadIntegrations = async () => {
    setIsLoadingIntegrations(true);

    try {
      const response = await getIntegrations({ clinicId });

      if (response?.integrations) {
        setIntegrations(response.integrations);
      }
    } catch (error: any) {
      console.error('Failed to load integrations:', error);
    } finally {
      setIsLoadingIntegrations(false);
    }
  };

  useEffect(() => {
    loadIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  const loadLogs = async (
    integrationId: number,
    startKey?: Record<string, any>,
    range?: { from: string; to: string }
  ) => {
    setIsLoadingLogs(true);

    try {
      const r = range || dateRange;
      const params: any = {
        integrationId,
        startDate: dayjs(r.from).startOf('day').valueOf(),
        endDate: dayjs(r.to).endOf('day').valueOf(),
      };

      if (startKey) {
        params.startKey = startKey;
      }

      const response = await getIntegrationLogs(params);

      if (response) {
        setLogs(response.logs ?? []);
        setLastKey(response.lastKey ?? null);
      }
    } catch (error: any) {
      console.error('Failed to load integration logs:', error);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const filteredLogs = useMemo(() => {
    if (!levelFilter) return logs;

    return logs.filter(log => log.level === levelFilter);
  }, [logs, levelFilter]);

  type AnalysisStatus = 'ok' | 'warn' | 'error';

  type QuickAnalysis = {
    status: AnalysisStatus;
    summary: string;
    details: { status: AnalysisStatus; text: string }[];
  };

  const quickAnalysis = useMemo((): QuickAnalysis | null => {
    if (!logs.length || !selectedIntegration) return null;

    const details: { status: AnalysisStatus; text: string }[] = [];
    let worstStatus: AnalysisStatus = 'ok';

    const escalate = (s: AnalysisStatus) => {
      if (s === 'error') worstStatus = 'error';
      else if (s === 'warn' && worstStatus !== 'error') worstStatus = 'warn';
    };

    // 1. Last poll recency
    const lastStart = logs.find(l => l.message.includes('Starting consolidated EDI processing'));

    if (lastStart) {
      const minutesAgo = Math.round((Date.now() - lastStart.createdAt) / 60000);

      if (minutesAgo <= 15) {
        details.push({ status: 'ok', text: `Last poll: ${minutesAgo} min ago` });
      } else if (minutesAgo <= 90) {
        details.push({
          status: 'ok',
          text: `Last poll: ${minutesAgo} min ago (may be in overnight/hourly schedule)`,
        });
      } else {
        details.push({ status: 'warn', text: `Last poll: ${minutesAgo} min ago — may be stalled` });
        escalate('warn');
      }
    } else {
      details.push({ status: 'warn', text: 'No polling activity found in loaded entries' });
      escalate('warn');
    }

    // 2. Error count
    const errorCount = logs.filter(l => l.level === 'error').length;

    if (errorCount === 0) {
      details.push({ status: 'ok', text: 'No errors' });
    } else {
      details.push({
        status: 'error',
        text: `${errorCount} error${errorCount > 1 ? 's' : ''} found`,
      });
      escalate('error');
    }

    // 3. Credentials
    if (selectedIntegration.credentialsValid === null) {
      details.push({ status: 'warn', text: 'Credentials: not checked' });
      escalate('warn');
    } else if (Number(selectedIntegration.credentialsValid) === 1) {
      details.push({ status: 'ok', text: 'Credentials: valid' });
    } else {
      details.push({ status: 'error', text: 'Credentials: invalid' });
      escalate('error');
    }

    // 4. Files discovered
    const discoverySummaries = logs.filter(l => l.message.includes('discovery summary'));
    let totalListed = 0;

    discoverySummaries.forEach(entry => {
      try {
        const parsed = ChiroUpJSON.parse(entry.data);

        if (parsed?.listedCount) {
          totalListed += parsed.listedCount;
        }
      } catch {
        // skip
      }
    });

    if (discoverySummaries.length > 0) {
      details.push({
        status: 'ok',
        text:
          totalListed > 0
            ? `Files discovered: ${totalListed} across ${discoverySummaries.length} checks`
            : `No files found in ${discoverySummaries.length} discovery checks (may be normal)`,
      });
    }

    // 5. Most recent cycle completed?
    const lastCompleted = logs.find(l =>
      l.message.includes('Completed consolidated EDI processing')
    );

    if (lastStart && lastCompleted) {
      if (lastCompleted.createdAt >= lastStart.createdAt) {
        details.push({ status: 'ok', text: 'Last polling cycle completed successfully' });
      } else {
        details.push({ status: 'warn', text: 'Last polling cycle may not have completed' });
        escalate('warn');
      }
    } else if (lastStart && !lastCompleted) {
      details.push({ status: 'warn', text: 'No completed cycle found in loaded entries' });
      escalate('warn');
    }

    const summaryMap: Record<AnalysisStatus, string> = {
      ok: 'EDI appears to be flowing normally.',
      warn: 'EDI may need attention.',
      error: 'EDI has problems that need investigation.',
    };

    return {
      status: worstStatus,
      summary: summaryMap[worstStatus],
      details,
    };
  }, [logs, selectedIntegration]);

  const onIntegrationClick = (row: IntegrationRecord) => {
    setSelectedIntegration(row);
    setLogs([]);
    setLastKey(null);
    setPrevKeys([]);
    setExpandedLogId(null);
    loadLogs(row.id);
  };

  const onLevelFilterChange = (level: string) => {
    const newLevel = levelFilter === level ? null : level;
    setLevelFilter(newLevel);
    setExpandedLogId(null);
  };

  const onDateRangeChange = (e: { from: string; to: string } | null) => {
    if (!e) return;

    const newRange = {
      from: e.from ? dayjs(e.from).format('YYYY-MM-DD') : dateRange.from,
      to: e.to ? dayjs(e.to).format('YYYY-MM-DD') : dateRange.to,
    };
    setDateRange(newRange);
    setLogs([]);
    setLastKey(null);
    setPrevKeys([]);
    setExpandedLogId(null);

    if (selectedIntegration) {
      loadLogs(selectedIntegration.id, undefined, newRange);
    }
  };

  const onNextPage = () => {
    if (selectedIntegration && lastKey) {
      setPrevKeys(prev => [...prev, lastKey]);
      loadLogs(selectedIntegration.id, lastKey);
    }
  };

  const onPrevPage = () => {
    if (selectedIntegration && prevKeys.length > 0) {
      const newPrevKeys = [...prevKeys];
      newPrevKeys.pop();
      const prevKey = newPrevKeys.length > 0 ? newPrevKeys[newPrevKeys.length - 1] : undefined;
      setPrevKeys(newPrevKeys);
      loadLogs(selectedIntegration.id, prevKey);
    }
  };

  const integrationSchema = useMemo(
    () => ({
      id: {
        label: 'ID',
        showOnList: true,
        td: (row: IntegrationRecord) => row.id,
      },
      name: {
        label: 'Name',
        showOnList: true,
        td: (row: IntegrationRecord) => (
          <span className="truncate max-w-48" title={row.name}>
            {row.name}
          </span>
        ),
      },
      integratedClearinghouse: {
        label: 'Clearinghouse',
        showOnList: true,
        td: (row: IntegrationRecord) => row.integratedClearinghouse ?? '—',
      },
      active: {
        label: 'Active',
        showOnList: true,
        td: (row: IntegrationRecord) => (
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${
              Number(row.active) === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {Number(row.active) === 1 ? 'Yes' : 'No'}
          </span>
        ),
      },
      production: {
        label: 'Prod',
        showOnList: true,
        td: (row: IntegrationRecord) => (
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${
              Number(row.production) === 1
                ? 'bg-green-100 text-green-800'
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            {Number(row.production) === 1 ? 'Yes' : 'No'}
          </span>
        ),
      },
      step: {
        label: 'Step',
        showOnList: true,
        td: (row: IntegrationRecord) => row.step ?? '—',
      },
      controlNumber: {
        label: 'Last Interchange #',
        showOnList: true,
        tdClassName: 'text-right tabular-nums',
        thClassName: 'text-right',
        td: (row: IntegrationRecord) => row.controlNumber?.toLocaleString() ?? '—',
      },
      credentialsValid: {
        label: 'Creds',
        showOnList: true,
        td: (row: IntegrationRecord) => {
          if (row.credentialsValid === null) return '—';

          return (
            <span
              className={`px-2 py-1 rounded text-xs font-semibold ${
                Number(row.credentialsValid) === 1
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {Number(row.credentialsValid) === 1 ? 'Valid' : 'Invalid'}
            </span>
          );
        },
      },
      lastProcessedAt: {
        label: 'Last Processed',
        showOnList: true,
        td: (row: IntegrationRecord) => {
          if (!row.lastProcessedAt) return '—';

          return ChiroUpDayJsCommon.display.datetimeWithTz(
            row.lastProcessedAt,
            tz,
            tz,
            'MMM D, YYYY h:mma',
            false
          );
        },
      },
    }),
    []
  );

  const parseLogData = (dataStr: string): Record<string, any> | null => {
    try {
      const parsed = ChiroUpJSON.parse(dataStr);

      if (!parsed || (typeof parsed === 'object' && Object.keys(parsed).length === 0)) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  };

  const formatValue = (val: any): string => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'number') return val.toLocaleString();
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'object') return ChiroUpJSON.stringify(val, '—');

    return String(val);
  };

  const logSchema = useMemo(
    () => ({
      createdAt: {
        label: 'Timestamp',
        showOnList: true,
        td: (row: LogRecord) => {
          return (
            <span className="whitespace-nowrap tabular-nums">
              {ChiroUpDayJsCommon.display.datetimeWithTz(
                row.createdAt,
                tz,
                tz,
                'MMM D h:mm:ss.SSSa',
                false
              )}
            </span>
          );
        },
      },
      level: {
        label: 'Level',
        showOnList: true,
        td: (row: LogRecord) => (
          <span
            className={`px-2 py-1 rounded text-xs font-semibold uppercase ${
              levelColors[row.level] ?? 'bg-gray-100 text-gray-700'
            }`}
          >
            {row.level}
          </span>
        ),
      },
      message: {
        label: 'Message',
        showOnList: true,
        td: (row: LogRecord) => (
          <span className="text-sm" title={row.message}>
            {row.message}
          </span>
        ),
      },
      data: {
        label: 'Data',
        showOnList: true,
        td: (row: LogRecord) => {
          const parsed = parseLogData(row.data);

          if (!parsed) return <span className="text-gray-400">—</span>;

          const entries = Object.entries(parsed);
          const isExpanded = expandedLogId === row.id;
          const previewEntries = entries.slice(0, 2);
          const hasMore = entries.length > 2;

          return (
            <div
              className={entries.length > 2 ? 'cursor-pointer' : ''}
              onClick={e => {
                if (entries.length <= 2) return;
                e.stopPropagation();
                setExpandedLogId(isExpanded ? null : row.id);
              }}
            >
              <dl className="text-xs space-y-0.5">
                {(isExpanded ? entries : previewEntries).map(([key, val]) => (
                  <div key={key} className="flex gap-2">
                    <dt className="text-gray-400 shrink-0">{key}:</dt>
                    <dd className="text-gray-700 break-all">{formatValue(val)}</dd>
                  </div>
                ))}
              </dl>
              {hasMore && !isExpanded ? (
                <span className="text-xs text-primary-500 mt-0.5 inline-block">
                  +{entries.length - 2} more
                </span>
              ) : null}
            </div>
          );
        },
      },
    }),
    [expandedLogId]
  );

  return (
    <div className="space-y-8">
      <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 flex flex-row justify-between items-center">
        <p className="text-sm">
          <strong>Clinic ID:</strong> {clinicId}
        </p>
        <button
          className="text-gray-400 hover:text-gray-600 cursor-pointer"
          onClick={() => setShowHelp(prev => !prev)}
          title="What is this?"
        >
          <QuestionMarkCircleIcon className="size-6" />
        </button>
      </div>

      {showHelp ? (
        <div className="border border-blue-300 bg-blue-50 rounded-lg p-4 text-sm space-y-2">
          <p>
            <strong>What does this screen show?</strong>
          </p>
          <p>
            This panel displays the automated EDI (Electronic Data Interchange) activity for this
            clinic. A cron job polls the clearinghouse on a schedule to upload pending 837 claims
            and download 835 remits, 277 status responses, and 999 acknowledgments.
          </p>
          <p>
            <strong>Integrations table:</strong> Shows the clinic's EDI integrations, their status,
            credentials, and when the last polling cycle ran. Click a row to view its logs.
          </p>
          <p>
            <strong>Integration logs:</strong> Activity from the DynamoDB{' '}
            <code className="text-xs bg-blue-100 px-1 rounded">app-prod-integration-logs</code>{' '}
            table (30-day TTL). Each polling cycle logs steps like "Uploading pending claims," "ERA
            files discovery summary," etc. The data column shows details like file counts and
            clearinghouse paths. Use the date range to control what is fetched. The level pills are
            a UI-only filter on the current page — they do not trigger a new query.
          </p>
          <p>
            <strong>What to look for:</strong> If EDI is flowing, you'll see regular polling cycles
            with "Starting consolidated EDI processing" and "Completed" entries. Check the discovery
            summaries for <code className="text-xs bg-blue-100 px-1 rounded">listedCount</code> to
            see if files were found. Error-level entries indicate failures.
          </p>
        </div>
      ) : null}

      {/* EDI Integrations */}
      <section className="bg-white border border-gray-400 inset-4 rounded-lg">
        <div className="p-4 flex flex-row justify-between">
          <h1 className="font-extrabold">EDI Integrations</h1>
          <Button
            text="Refresh"
            disabled={isLoadingIntegrations}
            loading={isLoadingIntegrations}
            onClick={loadIntegrations}
            type="button"
            icon={Icons.puzzle}
          />
        </div>

        <DatabaseTable
          instanceTitle="EDI Integrations"
          schema={integrationSchema}
          rows={integrations.length > 0 ? integrations : null}
          count={integrations.length}
          isFetching={isLoadingIntegrations}
          clickable={true}
          onRowClick={(row: IntegrationRecord) => onIntegrationClick(row)}
          options={{
            sectionClassName:
              'border border-gray-300 dark:border-dark-gray-600 rounded-md overflow-hidden',
            defaultTdClassName: 'px-6 py-2 align-middle',
            defaultThClassName:
              'px-6 py-2 text-left text-sm text-gray-900 dark:text-dark-gray-50 tracking-wide whitespace-nowrap',
            noRowsFoundComponent: (
              <NoRowsFound name="EDI Integrations">
                No EDI integrations found for this clinic.
              </NoRowsFound>
            ),
          }}
          parentClassName="p-4"
        />
      </section>

      {/* Integration Logs */}
      {selectedIntegration ? (
        <section className="bg-white border border-gray-400 inset-4 rounded-lg">
          <div className="p-4 flex flex-row justify-between items-center">
            <div>
              <h1 className="font-extrabold">Integration Logs: {selectedIntegration.name}</h1>
              <p className="text-xs text-gray-500 mt-1">
                Integration ID: {selectedIntegration.id} — DynamoDB TTL: 30 days
              </p>
            </div>
            <Loading flag={isLoadingLogs} style="tiny-inline-gray" />
          </div>

          {/* Quick Analysis */}
          {quickAnalysis && !isLoadingLogs ? (
            <div
              className={`mx-4 mb-4 p-3 rounded-lg border text-sm ${
                quickAnalysis.status === 'ok'
                  ? 'border-green-300 bg-green-50'
                  : quickAnalysis.status === 'warn'
                    ? 'border-amber-300 bg-amber-50'
                    : 'border-red-300 bg-red-50'
              }`}
            >
              <p
                className={`font-semibold ${
                  quickAnalysis.status === 'ok'
                    ? 'text-green-800'
                    : quickAnalysis.status === 'warn'
                      ? 'text-amber-800'
                      : 'text-red-800'
                }`}
              >
                {quickAnalysis.summary}
              </p>
              <ul className="mt-1.5 space-y-0.5">
                {quickAnalysis.details.map((d, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-gray-700">
                    <span
                      className={`inline-block size-2 rounded-full shrink-0 ${
                        d.status === 'ok'
                          ? 'bg-green-500'
                          : d.status === 'warn'
                            ? 'bg-amber-500'
                            : 'bg-red-500'
                      }`}
                    />
                    {d.text}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Filters */}
          <div className="px-4 pb-4 flex flex-row gap-4 items-end flex-wrap">
            <DateRangePicker label="Date Range" value={dateRange} onChange={onDateRangeChange} />
            <div className="flex flex-row gap-2 items-center pb-1">
              <span className="text-sm text-gray-500 mr-1">Level:</span>
              {LOG_LEVELS.map(level => (
                <PillButton
                  key={level}
                  isActive={levelFilter === level}
                  onClick={() => onLevelFilterChange(level)}
                >
                  <span className="uppercase text-xs">{level}</span>
                </PillButton>
              ))}
              {levelFilter ? (
                <button
                  className="text-xs text-gray-400 hover:text-gray-600 ml-2 cursor-pointer"
                  onClick={() => onLevelFilterChange(levelFilter)}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>

          <DatabaseTable
            instanceTitle="Integration Logs"
            schema={logSchema}
            rows={filteredLogs.length > 0 ? filteredLogs : null}
            count={filteredLogs.length}
            isFetching={isLoadingLogs}
            clickable={false}
            options={{
              sectionClassName:
                'border border-gray-300 dark:border-dark-gray-600 rounded-md overflow-hidden',
              defaultTdClassName: 'px-6 py-2 align-middle',
              defaultThClassName:
                'px-6 py-2 text-left text-sm text-gray-900 dark:text-dark-gray-50 tracking-wide whitespace-nowrap',
              noRowsFoundComponent: (
                <NoRowsFound name="Integration Logs">
                  {isLoadingLogs ? 'Loading logs...' : 'No logs found for the selected filters.'}
                </NoRowsFound>
              ),
            }}
            parentClassName="p-4"
          />

          {/* Pagination */}
          {prevKeys.length > 0 || lastKey ? (
            <div className="p-4 flex justify-between items-center">
              <Button
                text="Previous"
                disabled={isLoadingLogs || prevKeys.length === 0}
                onClick={onPrevPage}
                type="button"
              />
              <span className="text-xs text-gray-500">
                Page {prevKeys.length + 1}
                {logs.length > 0 ? ` — ${logs.length} entries` : ''}
              </span>
              <Button
                text="Next"
                disabled={isLoadingLogs || !lastKey}
                onClick={onNextPage}
                type="button"
              />
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default AutomationPanel;
